// ── components/ui.jsx ─────────────────────────────────────────────────────

// ── Dayrit Logo (faithful SVG recreation of blue→teal→green gradient circle) ──
export function DayritLogo({ size = 36 }) {
  const id = `dlg-${size}`;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={id} x1="15" y1="8" x2="85" y2="92" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   style={{ stopColor: 'var(--accent-a)' }} />
          <stop offset="42%"  style={{ stopColor: 'var(--accent)'   }} />
          <stop offset="100%" style={{ stopColor: 'var(--accent-c)' }} />
        </linearGradient>
      </defs>
      <circle cx="50" cy="50" r="50" fill={`url(#${id})`} />
      <path d="M21 53 L40 72 L79 29"
        stroke="white" strokeWidth="12.5"
        strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

export function LogoText({ size = 22 }) {
  return (
    <span style={{
      fontSize: size, fontWeight: 600, letterSpacing: '-0.3px',
      color: 'var(--text)', fontFamily: "'DM Sans', system-ui",
    }}>Dayrit</span>
  );
}

export function DayritWordmark({ height = 32 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
      <DayritLogo size={height} />
      <LogoText size={height * 0.62} />
    </div>
  );
}

// ── Button ────────────────────────────────────────────────────────────────
export function Button({ children, variant = 'primary', size = 'md', disabled, onClick, style, type = 'button', fullWidth }) {
  const base = {
    border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit', fontWeight: 600, transition: 'all 0.15s ease',
    opacity: disabled ? 0.4 : 1, display: 'inline-flex',
    alignItems: 'center', justifyContent: 'center', gap: 6,
    width: fullWidth ? '100%' : undefined, letterSpacing: '-0.1px', ...style,
  };
  const sizes = {
    sm: { fontSize: 12, padding: '6px 13px', borderRadius: 8 },
    md: { fontSize: 13, padding: '9px 17px', borderRadius: 9 },
    lg: { fontSize: 14, padding: '12px 24px', borderRadius: 10 },
  };
  const variants = {
    primary: { background: 'var(--brand-grad)', color: 'white', boxShadow: '0 2px 14px var(--brand-shadow)' },
    ghost:   { background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border)' },
    danger:  { background: 'rgba(239,68,68,0.09)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.2)' },
    surface: { background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)' },
  };
  return (
    <button type={type} disabled={disabled} onClick={onClick}
      style={{ ...base, ...sizes[size], ...variants[variant] }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.filter = 'brightness(1.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.filter = ''; }}
    >{children}</button>
  );
}

// ── Input ─────────────────────────────────────────────────────────────────
export function Input({ label, error, hint, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {label && <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', letterSpacing: '0.03em', textTransform: 'uppercase' }}>{label}</label>}
      <input {...props} style={{
        background: 'var(--input-bg)', border: `1px solid ${error ? '#EF4444' : 'var(--border)'}`,
        borderRadius: 9, padding: '10px 13px', fontSize: 14,
        color: 'var(--text)', fontFamily: 'inherit', outline: 'none',
        transition: 'all 0.15s', width: '100%', ...props.style,
      }}
        onFocus={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = '0 0 0 3px var(--accent-ring)'; }}
        onBlur={e  => { e.currentTarget.style.borderColor = error ? '#EF4444' : 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
      />
      {error && <span style={{ fontSize: 12, color: '#EF4444', fontWeight: 500 }}>{error}</span>}
      {hint && !error && <span style={{ fontSize: 12, color: 'var(--text3)' }}>{hint}</span>}
    </div>
  );
}

// ── Textarea ──────────────────────────────────────────────────────────────
export function Textarea({ label, error, rows = 3, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {label && <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', letterSpacing: '0.03em', textTransform: 'uppercase' }}>{label}</label>}
      <textarea {...props} rows={rows} style={{
        background: 'var(--input-bg)', border: `1px solid ${error ? '#EF4444' : 'var(--border)'}`,
        borderRadius: 9, padding: '10px 13px', fontSize: 14,
        color: 'var(--text)', fontFamily: 'inherit', outline: 'none',
        resize: 'vertical', transition: 'all 0.15s', ...props.style,
      }}
        onFocus={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = '0 0 0 3px var(--accent-ring)'; }}
        onBlur={e  => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
      />
      {error && <span style={{ fontSize: 12, color: '#EF4444' }}>{error}</span>}
    </div>
  );
}

// ── Select ────────────────────────────────────────────────────────────────
export function Select({ label, options, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {label && <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', letterSpacing: '0.03em', textTransform: 'uppercase' }}>{label}</label>}
      <select {...props} style={{
        background: 'var(--input-bg)', border: '1px solid var(--border)',
        borderRadius: 9, padding: '10px 13px', fontSize: 14,
        color: 'var(--text)', fontFamily: 'inherit', outline: 'none',
        cursor: 'pointer', width: '100%', ...props.style,
      }}
        onFocus={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = '0 0 0 3px var(--accent-ring)'; }}
        onBlur={e  => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────
export function Card({ children, style }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '20px', ...style }}>
      {children}
    </div>
  );
}

// ── Priority Badge ────────────────────────────────────────────────────────
const PRI = {
  high:   { color: '#DC2626', bg: 'rgba(220,38,38,0.08)',  dot: '#DC2626', label: 'High'   },
  medium: { color: '#D97706', bg: 'rgba(217,119,6,0.08)',  dot: '#D97706', label: 'Medium' },
  low:    { color: '#16A34A', bg: 'rgba(22,163,74,0.08)',  dot: '#16A34A', label: 'Low'    },
};
export function PriorityBadge({ priority }) {
  const p = PRI[priority] || PRI.medium;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 5, padding: '2px 7px', color: p.color, background: p.bg, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: p.dot, display: 'inline-block' }} />{p.label}
    </span>
  );
}

// ── Spinner ───────────────────────────────────────────────────────────────
export function Spinner({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ animation: 'spin 0.7s linear infinite', flexShrink: 0 }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <circle cx="12" cy="12" r="10" fill="none" stroke="var(--border)" strokeWidth="2.5"/>
      <path d="M12 2 a10 10 0 0 1 10 10" fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  );
}

// ── Progress Ring ─────────────────────────────────────────────────────────
export function ProgressRing({ done, total, size = 52 }) {
  const r = 20, circ = 2 * Math.PI * r, pct = total === 0 ? 0 : done / total;
  const cx = size / 2, cy = size / 2;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <defs>
          <linearGradient id="ring-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   style={{ stopColor: 'var(--accent-a)' }}/>
            <stop offset="100%" style={{ stopColor: 'var(--accent-c)' }}/>
          </linearGradient>
        </defs>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--border)" strokeWidth="3.5"/>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="url(#ring-grad)" strokeWidth="3.5"
          strokeDasharray={`${circ * pct} ${circ}`} strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.6s ease' }}/>
      </svg>
      <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 10, fontWeight: 700, color: 'var(--accent)' }}>
        {Math.round(pct * 100)}%
      </span>
    </div>
  );
}

// ── Empty State ───────────────────────────────────────────────────────────
export function EmptyState({ icon = '✅', title, sub }) {
  return (
    <div style={{ textAlign: 'center', padding: '56px 24px', color: 'var(--text3)' }}>
      <div style={{ fontSize: 38, marginBottom: 12, opacity: 0.45 }}>{icon}</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text2)', marginBottom: 5 }}>{title}</div>
      {sub && <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.6 }}>{sub}</div>}
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, width = 480 }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(3px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 14, padding: '24px', width: '100%', maxWidth: width,
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'modalIn 0.2s ease',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.3px' }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'var(--hover)', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 14, padding: '5px 7px', borderRadius: 6 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Sync Banner ───────────────────────────────────────────────────────────
export function SyncBanner({ count, onSync, syncing }) {
  return (
    <div style={{
      background: 'var(--brand-grad)', color: 'white',
      padding: '10px 16px', borderRadius: 10, marginBottom: 16,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 500 }}>
        <span>☁️</span>
        <span>{count} offline task{count !== 1 ? 's' : ''} ready to sync</span>
      </div>
      <button onClick={onSync} disabled={syncing} style={{
        background: 'rgba(255,255,255,0.22)', border: '1px solid rgba(255,255,255,0.35)',
        borderRadius: 7, padding: '5px 13px', color: 'white',
        fontSize: 12, fontWeight: 700, cursor: syncing ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6,
      }}>
        {syncing ? <><Spinner size={12} /> Syncing…</> : 'Sync now'}
      </button>
    </div>
  );
}

// ── Offline Badge ─────────────────────────────────────────────────────────
export function OfflineBadge() {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, background: 'rgba(217,119,6,0.1)',
      color: '#D97706', border: '1px solid rgba(217,119,6,0.22)',
      borderRadius: 4, padding: '1px 6px', letterSpacing: '0.04em',
    }}>OFFLINE</span>
  );
}

export { PRI as PRIORITY_COLORS };